"use client"

import { useState, useRef, useEffect, useCallback } from "react"
import { useSelector, useDispatch, useStore } from "react-redux"
import { useNavigate, useLocation } from "react-router-dom"
import {
  togglePanel,
  dismissPanel,
  resetUploadState,
  FILE_STATUS,
  applyAnalysisStatusMap,
  processingComplete,
} from "../../store/slices/assetUploadSlice"
import { refreshCache } from "../../store/slices/cacheRefreshSlice"
import { fetchUserMedias } from "../../store/slices/userSlice"
import { fetchMediaStats } from "../../store/slices/mediaStatsSlice"
import { fetchMediaReuseStats } from "../../store/slices/mediaReuseStatsSlice"
import { fetchMediaAnalysisStatus, extractMediaAssetStatusMap } from "../../store/slices/mediaAnalysisStatusSlice"
import { FOCUS_UPLOADED_ASSETS_KEY } from "./assetManagerHelpers"
import "./FloatingUploadManager.css"

const STATUS_LABELS = {
  [FILE_STATUS.UPLOADING]: "Uploading",
  [FILE_STATUS.PROCESSING]: "Analyzing",
  [FILE_STATUS.CATEGORIZING]: "Categorizing",
  [FILE_STATUS.READY]: "Ready",
  [FILE_STATUS.FAILED]: "Failed",
}

/** First status check runs immediately; later ticks are 20s apart. Each call checks a single pending media asset ID. */
const ANALYSIS_STATUS_INTERVAL_MS = 20_000
/** Safety cap: one attempt counts as one 20s tick (one ID checked per tick). */
const ANALYSIS_MAX_POLLS = 200

const FloatingUploadManager = () => {
  const dispatch = useDispatch()
  const store = useStore()
  const navigate = useNavigate()
  const location = useLocation()
  const uploadState = useSelector((state) => state.assetUpload) || {}
  const [showOverflowMenu, setShowOverflowMenu] = useState(false)
  const overflowRef = useRef(null)
  const filesRef = useRef([])
  const pollTimeoutRef = useRef(null)
  const panelRef = useRef(null)
  const dragState = useRef({ dragging: false, startX: 0, startY: 0, origX: 0, origY: 0 })
  const [panelPos, setPanelPos] = useState({ x: 0, y: 0 })
  const {
    phase = "idle",
    totalAssets = 0,
    processedCount = 0,
    uploadProgress = 0,
    failedCount = 0,
    files = [],
    isPanelExpanded = false,
    isDismissed = false,
    hideFloatingForFirstTimeUpload = false,
  } = uploadState
  filesRef.current = files

  const onDragStart = useCallback((e) => {
    if (e.target.closest("button, a, input, [role='option']")) return
    e.preventDefault()
    const rect = panelRef.current?.getBoundingClientRect()
    if (!rect) return
    dragState.current = { dragging: true, startX: e.clientX, startY: e.clientY, origX: panelPos.x, origY: panelPos.y }
    document.body.style.userSelect = "none"
  }, [panelPos])

  useEffect(() => {
    const HEADER_H = 56
    const onMove = (e) => {
      if (!dragState.current.dragging) return
      const dx = e.clientX - dragState.current.startX
      const dy = e.clientY - dragState.current.startY
      let newX = dragState.current.origX + dx
      let newY = dragState.current.origY + dy
      const el = panelRef.current
      if (el) {
        const w = el.offsetWidth
        const h = el.offsetHeight
        const vw = window.innerWidth
        const vh = window.innerHeight
        const defaultLeft = vw - 24 - w
        const defaultTop = vh - 24 - h
        const minX = -defaultLeft
        const maxX = 24
        const minY = HEADER_H - defaultTop
        const maxY = 24
        newX = Math.max(minX, Math.min(maxX, newX))
        newY = Math.max(minY, Math.min(maxY, newY))
      }
      setPanelPos({ x: newX, y: newY })
    }
    const onUp = () => {
      if (dragState.current.dragging) {
        dragState.current.dragging = false
        document.body.style.userSelect = ""
      }
    }
    window.addEventListener("mousemove", onMove)
    window.addEventListener("mouseup", onUp)
    return () => {
      window.removeEventListener("mousemove", onMove)
      window.removeEventListener("mouseup", onUp)
    }
  }, [])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (overflowRef.current && !overflowRef.current.contains(e.target)) {
        setShowOverflowMenu(false)
      }
    }
    if (showOverflowMenu) {
      document.addEventListener("mousedown", handleClickOutside)
    }
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [showOverflowMenu])

  useEffect(() => {
    return () => {
      filesRef.current.forEach((f) => {
        if (f.previewUrl) {
          try {
            URL.revokeObjectURL(f.previewUrl)
          } catch {
            /* ignore */
          }
        }
      })
    }
  }, [])

  const mediaIdsKey = files.map((f) => f.mediaAssetId ?? "").join(",")

  useEffect(() => {
    if (phase !== "processing") return

    let cancelled = false
    let attempts = 0

    const finish = async () => {
      const up0 = store.getState().assetUpload
      if (up0?.phase !== "complete") {
        dispatch(processingComplete())
      }
      const clientId = store.getState().assetUpload?.clientId
      if (clientId) {
        try { await dispatch(refreshCache()).unwrap() } catch {}
        dispatch(fetchMediaStats({ clientId, force: true }))
        dispatch(fetchUserMedias({ clientId, keepExistingDuringLoad: true, force: true }))
        dispatch(fetchMediaReuseStats({ clientId, force: true }))
      }
    }

    const scheduleNextTick = () => {
      if (cancelled) return
      pollTimeoutRef.current = setTimeout(runStatusTick, ANALYSIS_STATUS_INTERVAL_MS)
    }

    const isAllTerminal = (list) =>
      list.length > 0 && list.every((f) => f.status === FILE_STATUS.READY || f.status === FILE_STATUS.FAILED)

    /** One GET medias/analysis/status per call, single mediaAssetId; next tick (20s later) checks the next pending file. */
    const runStatusTick = async () => {
      if (cancelled) return
      if (store.getState().assetUpload?.phase !== "processing") return

      const up = store.getState().assetUpload
      const fileList = up.files || []

      const firstPending = fileList.find(
        (f) =>
          f.mediaAssetId != null &&
          f.status !== FILE_STATUS.READY &&
          f.status !== FILE_STATUS.FAILED,
      )

      if (!firstPending) {
        if (fileList.length === 0 || isAllTerminal(fileList)) {
          finish()
        } else {
          attempts += 1
          if (attempts >= ANALYSIS_MAX_POLLS) finish()
          else scheduleNextTick()
        }
        return
      }

      attempts += 1
      if (attempts > ANALYSIS_MAX_POLLS) {
        finish()
        return
      }

      try {
        const requestedIds = [firstPending.mediaAssetId]
        const raw = await dispatch(fetchMediaAnalysisStatus(requestedIds)).unwrap()
        const statusByAssetId = extractMediaAssetStatusMap(raw, requestedIds)
        if (Object.keys(statusByAssetId).length > 0) {
          dispatch(applyAnalysisStatusMap({ statusByAssetId }))
        }

        const upAfter = store.getState().assetUpload
        const filesAfter = upAfter.files || []

        if (upAfter.phase === "complete" || isAllTerminal(filesAfter)) {
          finish()
          return
        }
      } catch {
        /* next 20s tick retries the same pending asset */
      }

      if (store.getState().assetUpload?.phase === "complete") {
        finish()
        return
      }

      if (attempts >= ANALYSIS_MAX_POLLS) {
        finish()
        return
      }

      scheduleNextTick()
    }

    runStatusTick()

    return () => {
      cancelled = true
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current)
    }
  }, [phase, mediaIdsKey, dispatch, store])

  if (location.pathname.includes("/medialibrary")) return null

  if (phase === "idle" || isDismissed) return null

  if (hideFloatingForFirstTimeUpload && phase === "uploading") return null

  const canDismiss = phase === "complete" || phase === "failed" || phase === "cancelled"
  const hasFailures = failedCount > 0

  const revokePreviewUrls = () => {
    files.forEach((f) => {
      if (f.previewUrl) {
        try {
          URL.revokeObjectURL(f.previewUrl)
        } catch {
          /* ignore */
        }
      }
    })
  }

  const handleViewAssets = () => {
    setShowOverflowMenu(false)
    const focusUploadedAssets = {
      mediaAssetIds: files.map((f) => f.mediaAssetId).filter((id) => id != null && id !== ""),
      fileNames: files.map((f) => f.name).filter(Boolean),
    }
    const isProcessing = phase === "uploading" || phase === "processing"
    if (!isProcessing) {
      revokePreviewUrls()
      dispatch(dismissPanel())
      dispatch(resetUploadState())
    }
    navigate("/ai-asset-manager-details", {
      state: { [FOCUS_UPLOADED_ASSETS_KEY]: focusUploadedAssets },
    })
  }

  const handleDismiss = () => {
    setShowOverflowMenu(false)
    dispatch(dismissPanel())
    if (canDismiss) {
      revokePreviewUrls()
      setTimeout(() => dispatch(resetUploadState()), 300)
    }
  }

  const getStageTitle = () => {
    if (phase === "failed") return "Upload failed"
    if (phase === "cancelled") return "Processing stopped"
    if (phase === "complete") return "All files organized"
    if (phase === "uploading") return "Uploading files"
    if (phase === "processing") {
      return processedCount >= totalAssets * 0.5 ? "Categorizing assets" : "Analyzing assets"
    }
    return "Analyzing assets"
  }

  const getCollapsedLabel = () => {
    const getCount = () => {
      if (phase === "uploading") {
        const done = uploadProgress >= 100 ? totalAssets : Math.floor((uploadProgress / 100) * totalAssets)
        return `${done}/${totalAssets}`
      }
      if (phase === "processing" || phase === "cancelled") return `${processedCount}/${totalAssets}`
      if (phase === "complete") return `${totalAssets}/${totalAssets}`
      if (phase === "failed") return `${totalAssets - failedCount}/${totalAssets}`
      return `${processedCount}/${totalAssets}`
    }
    const action =
      phase === "uploading"
        ? "files uploaded"
        : phase === "complete"
          ? "files organized"
          : phase === "processing" && processedCount >= totalAssets * 0.5
            ? "files categorized"
            : phase === "processing" || phase === "cancelled"
              ? "files analyzed"
              : phase === "failed"
                ? "processed"
                : "processed"
    return { count: getCount(), action }
  }

  const getCurrentStep = () => {
    if (phase === "uploading") return 1
    if (phase === "processing") return processedCount >= totalAssets * 0.5 ? 3 : 2
    if (phase === "complete") return 3
    return 1
  }

  const getStageStep = () => {
    if (phase === "uploading") return "Step 1 of 3"
    if (phase === "processing") {
      return processedCount >= totalAssets * 0.5 ? "Step 3 of 3" : "Step 2 of 3"
    }
    if (phase === "complete") return "Complete"
    if (phase === "cancelled") return "Stopped"
    if (phase === "failed") return "Failed"
    return "Step 2 of 3"
  }

  const getBatchProgress = () => {
    if (phase === "complete") return 100
    if (phase === "cancelled") return Math.round((100 * processedCount) / Math.max(1, totalAssets))
    if (phase === "failed") return Math.round((100 * (totalAssets - failedCount)) / totalAssets)
    if (phase === "uploading") return Math.round(33 * (uploadProgress / 100))
    const processingPct = totalAssets > 0 ? processedCount / totalAssets : 0
    return Math.round(33 + 67 * processingPct)
  }

  const getProcessedLabel = () => {
    if (phase === "complete") return `${totalAssets} / ${totalAssets} files organized`
    if (phase === "cancelled") return `${processedCount} / ${totalAssets} processed`
    if (phase === "failed") return `${totalAssets - failedCount} / ${totalAssets} processed`
    if (phase === "uploading")
      return `${Math.floor((uploadProgress / 100) * totalAssets)} / ${totalAssets} files uploaded`
    if (phase === "processing" && processedCount >= totalAssets * 0.5) {
      return `${processedCount} / ${totalAssets} categorized`
    }
    return `${processedCount} / ${totalAssets} analyzed`
  }

  const getFileStatusLabel = (file) => STATUS_LABELS[file.status] ?? file.status

  const getFileNameParts = (name) => {
    if (!name || typeof name !== "string") return { base: name || "", ext: "" }
    const lastDot = name.lastIndexOf(".")
    if (lastDot <= 0) return { base: name, ext: "" }
    return { base: name.slice(0, lastDot), ext: name.slice(lastDot) }
  }

  return (
    <div
      className="fum-panel"
      ref={panelRef}
      style={{ transform: `translate(${panelPos.x}px, ${panelPos.y}px)` }}
    >
      <div
        className={`fum-collapsed ${isPanelExpanded ? "fum-collapsed--expanded" : ""}`}
        onMouseDown={onDragStart}
        onClick={() => {
          if (Math.abs(panelPos.x - (dragState.current.origX || 0)) < 4 && Math.abs(panelPos.y - (dragState.current.origY || 0)) < 4) {
            dispatch(togglePanel())
          }
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && dispatch(togglePanel())}
        style={{ cursor: "grab" }}
      >
        {!isPanelExpanded ? (
          <div className="fum-collapsed-compact">
            <div className="fum-header-row fum-header-row--compact">
              <span className="fum-collapsed-title">{getStageTitle()}</span>
              <div className="fum-header-actions">
                <button
                  type="button"
                  className="fum-view-files-link"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleViewAssets()
                  }}
                >
                  View files
                </button>
                <button
                  type="button"
                  className="fum-close-btn"
                  aria-label="Close"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDismiss()
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6L6 18" />
                    <path d="M6 6L18 18" />
                  </svg>
                </button>
              </div>
            </div>
            <span className="fum-collapsed-count">
              {(() => {
                const { count, action } = getCollapsedLabel()
                return (
                  <>
                    {count} {action}
                  </>
                )
              })()}
            </span>
            <div className="fum-collapsed-steps">
              {[1, 2, 3].map((step) => (
                <span
                  key={step}
                  className={`fum-step-dot ${getCurrentStep() >= step ? "fum-step-dot--active" : ""} ${getCurrentStep() === step ? "fum-step-dot--current" : ""}`}
                  aria-hidden
                />
              ))}
            </div>
            <div className="fum-collapsed-progress">
              <div className="fum-collapsed-progress-fill" style={{ width: `${getBatchProgress()}%` }} />
            </div>
          </div>
        ) : (
          <div className="fum-collapsed-content">
            <div className="fum-header-row">
              <div className="fum-header-text">
                <span className="fum-collapsed-title">{getStageTitle()}</span>
                <span className="fum-collapsed-step">{getStageStep()}</span>
              </div>
              <div className="fum-header-actions">
                <button
                  type="button"
                  className="fum-view-files-link"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleViewAssets()
                  }}
                >
                  View files
                </button>
                <button
                  type="button"
                  className="fum-close-btn"
                  aria-label="Close"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDismiss()
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6L6 18" />
                    <path d="M6 6L18 18" />
                  </svg>
                </button>
              </div>
            </div>
            <span className="fum-collapsed-progress-text">{getProcessedLabel()}</span>
            <div className="fum-collapsed-progress">
              <div className="fum-collapsed-progress-fill" style={{ width: `${getBatchProgress()}%` }} />
            </div>
          </div>
        )}
        <button
          type="button"
          className="fum-chevron"
          aria-label={isPanelExpanded ? "Collapse" : "Expand"}
          onClick={(e) => {
            e.stopPropagation()
            dispatch(togglePanel())
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            style={{ transform: isPanelExpanded ? "rotate(180deg)" : "none" }}
          >
            <path
              d="M19 9L12 16L5 9"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {isPanelExpanded && (
        <div className="fum-expanded">
          <div className="fum-file-list">
            {files.map((file) => {
              const { base, ext } = getFileNameParts(file.name)
              return (
                <div key={file.id} className={`fum-file-row fum-file-row--${file.status}`}>
                  <div className="fum-file-thumb">
                    {file.previewUrl && (file.type === "image" || file.type === "video") ? (
                      file.type === "video" ? (
                        <div className="fum-thumb-video">
                          <video src={file.previewUrl} muted preload="metadata" />
                          <span className="fum-thumb-play" aria-hidden>
                            ▶
                          </span>
                        </div>
                      ) : (
                        <img src={file.previewUrl} alt="" className="fum-thumb-img" />
                      )
                    ) : (
                      <div className={`fum-thumb-icon fum-thumb-icon--${file.type}`}>
                        {file.type === "video" ? (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="2" y="4" width="20" height="16" rx="2" />
                            <path d="M10 9l6 3-6 3V9z" />
                          </svg>
                        ) : (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="4" y="2" width="16" height="20" rx="2" />
                            <path d="M8 8h8M8 12h8M8 16h4" />
                          </svg>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="fum-file-info">
                    <span className="fum-file-name" title={file.name}>
                      <span className="fum-file-name-base">{base}</span>
                      {ext && <span className="fum-file-name-ext">{ext}</span>}
                    </span>
                  </div>
                  <span className="fum-file-status">{getFileStatusLabel(file)}</span>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default FloatingUploadManager
